import { HttpResponse } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import 'rxjs/add/operator/takeUntil';
import 'rxjs/add/observable/timer';
import { EmployeeProbationResponse } from '../../common/employee-probation/employee-probation.models';
import { Group } from '../../../models/group.model';
import { GroupContext } from '../../../models/group-context.model';
import { User } from '../../../models/user.model';
import { dealPayrollGradeKey } from '../../../models/deal-payroll-presentation';
import { DEFAULT_VALUES, GROUP_CONSTANTS } from '../../../shared.constants/all-constants';
import {
	PUBLIC_ABSENCE_STATUSES,
	PublicAbsenceStatus
} from '../../common/vacation/vacation-api.constants';
import { VacationGroupPresenceResponse } from '../../common/vacation/vacation.models';
import {
	VIEW_GROUP_EMPTY_COUNT,
	VIEW_GROUP_EMPTY_TEXT,
	VIEW_GROUP_FIRST_DISPLAY_INDEX,
	VIEW_GROUP_MISSING_INDEX,
	VIEW_GROUP_NOT_AVAILABLE_TEXT,
	VIEW_GROUP_SCHEDULE_LABELS
} from './view-group.constants';
import { ViewGroupDataPort } from './view-group-data.port';
import { ViewGroupIdentityState } from './view-group-identity.state';
import {
	ScheduleTitleRecord,
	ScheduleTitleSegment,
	SortCriterion,
	SortIndexByUserOrder,
	SortState,
	VacationPresenceByUserId,
	VectorUserListResponse,
	ViewGroupIdentityContext,
	ViewGroupScheduleSlots,
	ViewGroupShiftKind,
	ViewGroupShiftSchedule,
	ViewGroupVectorUser
} from './view-group.types';

const SHIFT_LABELS = {FIRST: '1', SECOND: '2', THIRD: '3', FLEXIBLE: 'F'};
const SHIFT_CURRENT = 'current';
const SHIFT_MISSING = '—';
const SHIFT_HOURS_SEPARATOR = '–';
const DEPARTMENT_TYPE = 'DEPARTMENT';
const GRADE_UNASSIGNED_KEY = 'DEPARTMENT_GRADE_UNASSIGNED';
const GRADE_UPDATING = 'UPDATING';
const GRADE_UNAVAILABLE = 'UNAVAILABLE';
const GRADE_UNASSIGNED = 'UNASSIGNED';
const GRADE_STATUS_PREFIX = 'DEPARTMENT_GRADE_';
const VECTOR_COLUMNS = ['SKILLS', 'LOYALTY', 'GRAVITY', 'INTEGRATION', 'SPEED'];
const PROBATION_CHECKING = 'PROBATION_CHECKING_RESULT';
const PROBATION_RETRY = 'PROBATION_RETRY_ACTION';
const PROBATION_UPDATE_ERROR = 'PROBATION_UPDATE_ERROR';

@Injectable()
export class ViewGroupRosterCoordinator implements OnDestroy {
	private readonly shiftLabels = [SHIFT_LABELS.FIRST, SHIFT_LABELS.SECOND, SHIFT_LABELS.THIRD, SHIFT_LABELS.FLEXIBLE];
	users: ViewGroupVectorUser[] = [];
	probationBusy: {[userId: string]: boolean} = {};
	probationMessages: {[userId: string]: string} = {};
	loading = false;
	loadError = false;
	readonly vectorColumns = VECTOR_COLUMNS;
	presenceByUserId: VacationPresenceByUserId = {};
	sortCriteria: SortCriterion[] = [this.lastNameCriterion(GROUP_CONSTANTS.SORT_DESC)];
	sortState: SortState = this.initialSortState();
	sortIndexByUserOrder: SortIndexByUserOrder = {};
	sortByNativeName = false;
	hasActiveSort = false;
	private includeSubdepartments = false;

	private readonly destroy$ = new Subject<void>();
	private readonly vectorsChanged$ = new Subject<void>();
	private readonly presenceChanged$ = new Subject<void>();

	constructor(
		private data: ViewGroupDataPort,
		private identity: ViewGroupIdentityState
	) {}

	currentUser(): User {
		return this.data.currentUser();
	}

	routeGroupId(): string | number {
		return this.data.routeGroupId();
	}

	routeGroupIds(): Observable<string | number> {
		return this.data.routeGroupIds();
	}

	isReportMode(): boolean {
		return this.data.isReportMode();
	}

	loadGroup(groupId: string | number): Observable<HttpResponse<Group>> {
		return this.data.loadGroup(groupId);
	}

	loadGroupContext(groupId: string | number): Observable<HttpResponse<GroupContext>> {
		return this.data.loadGroupContext(groupId);
	}

	loadHeadGroups(): Observable<HttpResponse<Group[]>> {
		return this.data.loadHeadGroups();
	}

	loadAllGroups(): Observable<HttpResponse<Group[]>> {
		return this.data.loadAllGroups();
	}

	deleteMember(userId: string | number, groupId: string): Observable<HttpResponse<Object>> {
		return this.data.deleteMember(userId, groupId);
	}

	replaceIdentity(context: ViewGroupIdentityContext): void {
		if (this.identity.group !== context.group) {
			this.vectorsChanged$.next();
			this.users = [];
			this.loading = false;
			this.loadError = false;
			this.resetVacationPresence();
			this.includeSubdepartments = false;
		}
		this.identity.replace(context);
		this.acceptDepartmentScope();
	}

	private acceptDepartmentScope(): void {
		const combined = this.identity.hasCurrentGroupContext && this.identity.group.groupType === DEPARTMENT_TYPE &&
			!this.identity.groupContext.parentDepartment &&
			this.identity.groupContext.canReadSalaryReport === true;
		if (combined === this.includeSubdepartments) { return; }
		this.includeSubdepartments = combined;
		if (!this.identity.group) { return; }
		this.loadVectors(this.identity.group.id, this.identity.isAdmin, this.sortCriteria);
		this.loadVacationPresence(this.identity.group.id);
	}

	loadVectors(groupId: string, isAdmin: boolean, criteria: SortCriterion[]): void {
		this.vectorsChanged$.next();
		this.probationBusy = {};
		this.loading = true;
		this.loadError = false;
		const request = this.includeSubdepartments ? this.data.loadCombinedVectors(groupId, criteria) :
			this.data.loadVectors(groupId, isAdmin, criteria);
		request
			.takeUntil(this.vectorsChanged$).takeUntil(this.destroy$).subscribe(
				response => this.acceptVectors(response),
				() => this.failVectors()
			);
	}

	retry(): void {
		if (!this.identity.group || this.loading) { return; }
		this.loadVectors(this.identity.group.id, this.identity.isAdmin, this.sortCriteria);
	}

	private acceptVectors(response: VectorUserListResponse): void {
		if (!response.body || !Array.isArray(response.body.users)) {
			this.failVectors();
			return;
		}
		this.users = response.body.users.map(row => this.presentPayroll(row));
		this.settleProbationMessages();
		this.scheduleProbationExpiry();
		this.loading = false;
		this.loadError = false;
	}

	canApproveProbation(row: ViewGroupVectorUser): boolean {
		const status = row.probation;
		return !!status && !status.approved && status.canApproveDuringDeployment === true &&
			Date.parse(status.windowExpiresAt) > Date.now() && !this.probationBusy[row.user.id];
	}

	approveProbation(row: ViewGroupVectorUser, event: Event): void {
		event.stopPropagation();
		(event.target as HTMLInputElement).checked = false;
		if (!this.canApproveProbation(row)) { return; }
		const userId = row.user.id;
		this.probationBusy[userId] = true;
		this.probationMessages[userId] = VIEW_GROUP_EMPTY_TEXT;
		this.data.approveProbationDuringDeployment(userId).takeUntil(this.vectorsChanged$).takeUntil(this.destroy$)
			.subscribe(response => this.acceptProbation(row, response.body), () => this.recheckProbation(userId));
	}

	private acceptProbation(row: ViewGroupVectorUser, status: EmployeeProbationResponse): void {
		this.probationBusy[row.user.id] = false;
		if (!status || status.userId !== row.user.id || !status.approved) {
			this.recheckProbation(row.user.id); return;
		}
		row.probation = {approved: true, approvedAt: status.approvedAt, canApproveDuringDeployment: false};
	}

	private recheckProbation(userId: string): void {
		this.probationBusy[userId] = false;
		this.probationMessages[userId] = PROBATION_CHECKING;
		this.retry();
	}

	private settleProbationMessages(): void {
		this.users.forEach(row => {
			if (this.probationMessages[row.user.id] !== PROBATION_CHECKING) { return; }
			this.probationMessages[row.user.id] = row.probation && row.probation.approved ? VIEW_GROUP_EMPTY_TEXT : PROBATION_RETRY;
		});
	}

	private scheduleProbationExpiry(): void {
		const deadlines = this.users.filter(row => row.probation && row.probation.canApproveDuringDeployment)
			.map(row => Date.parse(row.probation.windowExpiresAt)).filter(time => isFinite(time) && time > Date.now());
		if (!deadlines.length) { return; }
		Observable.timer(Math.max(VIEW_GROUP_EMPTY_COUNT, Math.min.apply(null, deadlines) - Date.now()))
			.takeUntil(this.vectorsChanged$).takeUntil(this.destroy$).subscribe(() => this.expireProbationActions());
	}

	private expireProbationActions(): void {
		this.users.forEach(row => {
			if (row.probation && Date.parse(row.probation.windowExpiresAt) <= Date.now()) {
				row.probation.canApproveDuringDeployment = false;
			}
		});
		this.scheduleProbationExpiry();
	}

	private failVectors(): void {
		this.loading = false;
		this.loadError = true;
		Object.keys(this.probationMessages).forEach(id => {
			if (this.probationMessages[id] === PROBATION_CHECKING) { this.probationMessages[id] = PROBATION_UPDATE_ERROR; }
		});
	}

	private presentPayroll(row: ViewGroupVectorUser): ViewGroupVectorUser {
		if (!row.payrollCalculationMethod) { return row; }
		if (row.currentGradeStatus === GRADE_UPDATING || row.currentGradeStatus === GRADE_UNAVAILABLE) {
			return Object.assign({}, row, {currentGradeLabelKey: GRADE_STATUS_PREFIX + row.currentGradeStatus});
		}
		return Object.assign({}, row, {currentGradeLabelKey:
			row.currentGrade && row.currentGradeStatus !== GRADE_UNASSIGNED ? dealPayrollGradeKey(row.currentGrade) : GRADE_UNASSIGNED_KEY});
	}

	loadVacationPresence(groupId: string): void {
		this.resetVacationPresence();
		this.data.loadVacationPresence(groupId, this.includeSubdepartments)
			.takeUntil(this.presenceChanged$)
			.takeUntil(this.destroy$)
			.subscribe(
				response => this.applyVacationPresence(response),
				() => this.resetVacationPresence()
			);
	}

	resetVacationPresence(): void {
		this.presenceChanged$.next();
		this.presenceByUserId = {};
	}

	trackByUserId(_index: number, vectorUser: ViewGroupVectorUser): string {
		return vectorUser.user.id;
	}

	trackByColumn(_index: number, column: string): string {
		return column;
	}

	trackByGroup(_index: number, group: Group): string {
		return group.id;
	}

	trackByIndex(index: number, _item: Object): number {
		return index;
	}

	change(userOrder: string, order: string): void {
		if (this.sortState[userOrder] === order) {
			this.remove(userOrder);
			return;
		}
		this.disableNativeNameSort();
		this.sortState[userOrder] = order;
		this.upsertCriterion(userOrder, order);
		this.sortCriteria = this.sortCriteria.filter(
			criterion => criterion.userOrder !== GROUP_CONSTANTS.SORT_BY_LAST_NAME
		);
		this.completeSortChange();
	}

	clear(): void {
		Object.keys(this.sortState).forEach(
			key => this.sortState[key] = DEFAULT_VALUES.NULL_VALUE
		);
		this.sortState[GROUP_CONSTANTS.SORT_BY_LAST_NAME] = DEFAULT_VALUES.NULL_VALUE;
		this.sortCriteria = [this.lastNameCriterion(GROUP_CONSTANTS.SORT_DESC)];
		this.completeSortChange();
	}

	remove(userOrder: string): void {
		this.sortCriteria = this.sortCriteria.filter(
			criterion => criterion.userOrder !== userOrder
		);
		this.sortState[userOrder] = DEFAULT_VALUES.NULL_VALUE;
		if (this.sortCriteria.length === VIEW_GROUP_EMPTY_COUNT) {
			this.sortState[GROUP_CONSTANTS.SORT_BY_LAST_NAME] = DEFAULT_VALUES.NULL_VALUE;
			this.sortCriteria.push(this.lastNameCriterion(GROUP_CONSTANTS.SORT_DESC));
		}
		this.completeSortChange();
	}

	toggleNativeName(): void {
		this.sortByNativeName = !this.sortByNativeName;
		if (this.sortByNativeName) {
			this.activateNativeNameSort();
		} else {
			this.activateLastNameSort();
		}
		this.completeSortChange();
	}

	title(record: ScheduleTitleRecord, from: string = VIEW_GROUP_EMPTY_TEXT): string {
		const workStart = record.workStart || VIEW_GROUP_NOT_AVAILABLE_TEXT;
		const workEnd = record.workEnd || VIEW_GROUP_NOT_AVAILABLE_TEXT;
		const period = (record.segments || []).map(segment => this.scheduleSegment(segment))
			.join(VIEW_GROUP_SCHEDULE_LABELS.SEPARATOR);
		const scheduleStart = from === VIEW_GROUP_EMPTY_TEXT
			? VIEW_GROUP_EMPTY_TEXT
			: VIEW_GROUP_SCHEDULE_LABELS.START_OF_SCHEDULER + from + VIEW_GROUP_SCHEDULE_LABELS.SPACE;
		return scheduleStart + VIEW_GROUP_SCHEDULE_LABELS.START_WORK + workStart +
			VIEW_GROUP_SCHEDULE_LABELS.END_WORK + workEnd +
			VIEW_GROUP_SCHEDULE_LABELS.PERIOD + period;
	}

	shiftLabel(row: ViewGroupVectorUser, kind: ViewGroupShiftKind, group: ViewGroupScheduleSlots): string {
		const serverLabel = kind === SHIFT_CURRENT ? row.currentShiftLabel : row.plannedShiftLabel;
		if (this.shiftLabels.indexOf(serverLabel) !== VIEW_GROUP_MISSING_INDEX) { return serverLabel; }
		const assignment = row.shift && row.shift[kind];
		if (row.user.mainGroup && group && row.user.mainGroup === group.id) {
			if (!assignment && kind === SHIFT_CURRENT && group.workSchedule && group.workSchedule.id) { return SHIFT_LABELS.FIRST; }
			const label = this.ownShiftLabel(assignment && assignment.schedule, group);
			if (label) { return label; }
		}
		const schedule = assignment && assignment.schedule;
		return schedule && schedule.workStart && schedule.workEnd ?
			schedule.workStart + SHIFT_HOURS_SEPARATOR + schedule.workEnd : SHIFT_MISSING;
	}

	shiftTitle(row: ViewGroupVectorUser, kind: ViewGroupShiftKind, group: ViewGroupScheduleSlots): string {
		const assignment = row.shift && row.shift[kind];
		if (assignment && assignment.schedule) { return this.title(assignment.schedule, assignment.from); }
		if (!assignment && kind === SHIFT_CURRENT && row.user.mainGroup && group &&
			row.user.mainGroup === group.id && group.workSchedule) { return this.title(group.workSchedule); }
		return VIEW_GROUP_EMPTY_TEXT;
	}

	private ownShiftLabel(schedule: ViewGroupShiftSchedule, group: ViewGroupScheduleSlots): string {
		if (!schedule || !schedule.id) { return VIEW_GROUP_EMPTY_TEXT; }
		const slots = [group.workSchedule, group.workScheduleId2, group.workScheduleId3];
		const index = slots.findIndex(slot => this.sameSchedule(slot, schedule));
		if (index !== VIEW_GROUP_MISSING_INDEX) { return this.shiftLabels[index]; }
		return (group.flexibleWorkSchedule || []).some(slot => this.sameSchedule(slot, schedule)) ?
			SHIFT_LABELS.FLEXIBLE : VIEW_GROUP_EMPTY_TEXT;
	}

	private sameSchedule(slot: ViewGroupShiftSchedule, schedule: ViewGroupShiftSchedule): boolean {
		return !!slot && !!slot.id && slot.id === schedule.id &&
			(!slot.type || !schedule.type || slot.type === schedule.type);
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
		this.vectorsChanged$.complete();
		this.presenceChanged$.complete();
	}

	private applyVacationPresence(response: VacationGroupPresenceResponse): void {
		const presenceByUserId: VacationPresenceByUserId = {};
		response.users.forEach(presence => {
			if (this.isSupportedPresence(presence.status)) {
				presenceByUserId[presence.userId] = presence.status;
			}
		});
		this.presenceByUserId = presenceByUserId;
	}

	private isSupportedPresence(
		status: PublicAbsenceStatus | null
	): status is PublicAbsenceStatus {
		return !!status && PUBLIC_ABSENCE_STATUSES.some(
			supportedStatus => supportedStatus === status
		);
	}

	private initialSortState(): SortState {
		const state: SortState = {};
		state[GROUP_CONSTANTS.SORT_BY_SKILLS] = DEFAULT_VALUES.NULL_VALUE;
		state[GROUP_CONSTANTS.SORT_BY_LOYALTY] = DEFAULT_VALUES.NULL_VALUE;
		state[GROUP_CONSTANTS.SORT_BY_GRAVITY] = DEFAULT_VALUES.NULL_VALUE;
		state[GROUP_CONSTANTS.SORT_BY_INTEGRATION] = DEFAULT_VALUES.NULL_VALUE;
		state[GROUP_CONSTANTS.SORT_BY_SPEED] = DEFAULT_VALUES.NULL_VALUE;
		state[GROUP_CONSTANTS.SORT_BY_LAST_NAME] = GROUP_CONSTANTS.SORT_ASC;
		state[GROUP_CONSTANTS.SORT_BY_FULL_NATIVE_NAME] = DEFAULT_VALUES.NULL_VALUE;
		return state;
	}

	private disableNativeNameSort(): void {
		if (!this.sortByNativeName) {
			return;
		}
		this.sortByNativeName = false;
		this.sortState[GROUP_CONSTANTS.SORT_BY_FULL_NATIVE_NAME] = DEFAULT_VALUES.NULL_VALUE;
	}

	private upsertCriterion(userOrder: string, order: string): void {
		const index = this.sortCriteria.findIndex(
			criterion => criterion.userOrder === userOrder
		);
		if (index !== VIEW_GROUP_MISSING_INDEX) {
			this.sortCriteria[index].order = order;
			return;
		}
		this.sortCriteria.push({ userOrder: userOrder, order: order });
	}

	private activateNativeNameSort(): void {
		Object.keys(this.sortState).forEach(key => {
			if (key !== GROUP_CONSTANTS.SORT_BY_FULL_NATIVE_NAME &&
				key !== GROUP_CONSTANTS.SORT_BY_LAST_NAME) {
				this.sortState[key] = DEFAULT_VALUES.NULL_VALUE;
			}
		});
		this.sortState[GROUP_CONSTANTS.SORT_BY_FULL_NATIVE_NAME] = GROUP_CONSTANTS.SORT_ASC;
		this.sortState[GROUP_CONSTANTS.SORT_BY_LAST_NAME] = DEFAULT_VALUES.NULL_VALUE;
		this.sortCriteria = [this.nativeNameCriterion()];
	}

	private activateLastNameSort(): void {
		this.sortState[GROUP_CONSTANTS.SORT_BY_FULL_NATIVE_NAME] = DEFAULT_VALUES.NULL_VALUE;
		this.sortState[GROUP_CONSTANTS.SORT_BY_LAST_NAME] = GROUP_CONSTANTS.SORT_ASC;
		this.sortCriteria = [this.lastNameCriterion(GROUP_CONSTANTS.SORT_ASC)];
	}

	private completeSortChange(): void {
		this.refreshSortPresentation();
		this.loadVectors(this.identity.group.id, this.identity.isAdmin, this.sortCriteria);
	}

	private refreshSortPresentation(): void {
		this.sortIndexByUserOrder = {};
		this.sortCriteria.forEach((criterion, index) => {
			this.sortIndexByUserOrder[criterion.userOrder] =
				index + VIEW_GROUP_FIRST_DISPLAY_INDEX;
		});
		this.hasActiveSort = Object.keys(this.sortState).some(
			key => this.isActiveVectorSort(key)
		);
	}

	private isActiveVectorSort(key: string): boolean {
		return this.sortState[key] !== DEFAULT_VALUES.NULL_VALUE &&
			key !== GROUP_CONSTANTS.SORT_BY_FULL_NATIVE_NAME &&
			key !== GROUP_CONSTANTS.SORT_BY_LAST_NAME;
	}

	private lastNameCriterion(order: string): SortCriterion {
		return { userOrder: GROUP_CONSTANTS.SORT_BY_LAST_NAME, order: order };
	}

	private nativeNameCriterion(): SortCriterion {
		return {
			userOrder: GROUP_CONSTANTS.SORT_BY_FULL_NATIVE_NAME,
			order: GROUP_CONSTANTS.SORT_ASC
		};
	}

	private scheduleSegment(segment: ScheduleTitleSegment): string {
		return VIEW_GROUP_SCHEDULE_LABELS.WORK + segment.workDays +
			VIEW_GROUP_SCHEDULE_LABELS.REST + segment.restDays;
	}
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/views/view-group/view-group-roster.coordinator.ts